import { useAppDispatch, useAppSelector } from 'app/hooks';
import { VideoFunctionListItem } from 'features/VideoFunction/models/videoFunction';
import { mutliDragAwareReorder } from 'features/VideoFunction/utils/dragutils/utils';
import { selectVideoFunctionSelectedItemIds, videoFunctionActions } from 'features/VideoFunction/videoFunctionSlice';
import { createContext, memo } from 'react';
import { DragDropContext, DraggingStyle, DragStart, DropResult, NotDraggingStyle } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import VideoFunctionRowList from '../VideoFunctionRowList';

export interface VideoFunctionContextProps {
  getListStyle: (isDraggingOver: boolean) => object;
  getItemStyle: (
    isDragging: boolean,
    draggableStyle: DraggingStyle | NotDraggingStyle | undefined,
    isSelected?: boolean,
    isGhosting?: boolean
  ) => any;
  onSelectedRowChanged?: () => void;
  onRestoreVideoFunction?: (videoFuctionId: number) => void;
}

const getListStyle = (isDraggingOver: boolean) => ({
  background: isDraggingOver ? '#999' : 'lightgrey',
});

const getItemStyle = (isDragging: boolean, draggableStyle: any, isSelected?: boolean, isGhosting?: boolean) => {
  let backgroundColor = '#fff';

  if (isGhosting) {
    backgroundColor = '#FAFBFC';
  }

  if (isSelected) {
    backgroundColor = '#DEEBFF';
  }

  if (isDragging) {
    backgroundColor = '#ffff8d';
  }

  return {
    userSelect: 'none',
    background: backgroundColor,
    opacity: isGhosting ? '0' : '1',
    ...draggableStyle,
  };
};

export const COLUMN_WIDTH = [5, 35, 60];
export const VideoFunctionContext = createContext<VideoFunctionContextProps>({
  getListStyle: getListStyle,
  getItemStyle: getItemStyle,
});

interface VideoFunctionListTableProps {
  videoFunctionList: VideoFunctionListItem[];
  onPriorityChange: (hasChanged: boolean) => void;
  onRestoreVideoFunction: (videoFunctionId: number) => void;
  onSelectedRowChanged: () => void;
  onOpenEditVideoFunctionModal: ({
    videoFunctionId,
    videoFunctionName,
  }: {
    videoFunctionId?: number;
    videoFunctionName?: string;
  }) => void;
}

function VideoFunctionListTable({
  videoFunctionList,
  onRestoreVideoFunction,
  onSelectedRowChanged,
  onPriorityChange,
}: VideoFunctionListTableProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  // state
  const selectedItemIds = useAppSelector(selectVideoFunctionSelectedItemIds);

  function checkPriorityChange(oldList: VideoFunctionListItem[], newList: VideoFunctionListItem[]): boolean {
    if (oldList.length !== newList.length) return true;
    return oldList.some((item, index) => item.id !== newList[index].id || item.priority !== newList[index].priority);
  }

  function onDragStart(start: DragStart) {
    const id: string = start.draggableId;
    const selected = selectedItemIds.find((itemId: string): boolean => itemId === id);

    // if dragging an item that is not selected - unselect all items
    if (!selected) {
      dispatch(videoFunctionActions.unselectAll());
    }
    dispatch(videoFunctionActions.setDraggingItemId(id));
  }

  function onDragEnd(result: DropResult) {
    const destination = result.destination;
    const source = result.source;
    const oldList = [...videoFunctionList];

    // nothing to do
    if (!destination || result.reason === 'CANCEL') {
      dispatch(videoFunctionActions.setDraggingItemId(undefined));
      return;
    }

    const droppableId = result.source.droppableId;
    const reorderData = mutliDragAwareReorder({
      data: videoFunctionList,
      selectedItemIds,
      source,
      destination,
      droppableId,
    });
    const hasChanged = checkPriorityChange(oldList, reorderData.data);
    onPriorityChange(hasChanged);
    dispatch(
      videoFunctionActions.updateState({
        ...reorderData,
        selectedDroppableId: undefined,
        draggingItemId: undefined,
      })
    );
  }

  return (
    <VideoFunctionContext.Provider
      value={{
        getListStyle: getListStyle,
        getItemStyle: getItemStyle,
        onSelectedRowChanged: onSelectedRowChanged,
        onRestoreVideoFunction: onRestoreVideoFunction,
      }}
    >
      <div className="dan-sticky-table flex-grow-1 flex-shrink-1">
        <div className="dan-table">
          <div className="dan-thead">
            <div className="dan-tr">
              <div className="dan-th flex-grow-0 flex-shrink-0" style={{ width: COLUMN_WIDTH[0] + '%' }}>
                {t('videoFunctionListPage.choice')}
              </div>
              <div className="dan-th flex-grow-0 flex-shrink-0" style={{ width: COLUMN_WIDTH[1] + '%' }}>
                {t('videoFunctionListPage.video_name')}
              </div>
              <div className="dan-th flex-grow-0 flex-shrink-0" style={{ width: COLUMN_WIDTH[2] + '%' }}>
                {t('videoFunctionListPage.remarks')}
              </div>
            </div>
          </div>
          <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
            <VideoFunctionRowList data={videoFunctionList} />
          </DragDropContext>
        </div>
      </div>
    </VideoFunctionContext.Provider>
  );
}

export default memo(VideoFunctionListTable);
